import React, { Suspense, lazy, useRef, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import HomePageOrPartnerPage, { getHomeFromStorage, getHomeSlugAndType } from './components/HomePageOrPartnerPage/HomePageOrPartnerPage';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { PageShimmer } from './components/Shimmer/PageShimmer';
import { ClientProvider, useClient } from './contexts/ClientContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { AlertProvider } from './context/AlertContext';
import GlobalAlert from './components/GlobalAlert/GlobalAlert';
import { AuthProvider } from './contexts/AuthContext';
import { startTTLCleanup } from './utils/ttlStorage';
import { isPathBlocked } from './config/blockAccess';
import './styles/common.css';

/**
 * Block listed routes and show the coming-soon page instead.
 * Remove any path from this list to re-enable that page.
 * Paths must match the URL (e.g. "/jobs", "/p/my-custom-page").
 * Login and signup are omitted so users can still sign in.
 */
export const BLOCK_ACCESS = [
  // '/',
  // '/job-search',
  // '/jobs',
  // '/recommended-jobs',
  // '/saved-jobs',
  // '/request-demo',
  // '/recruitment-solutions',
  // '/branded-company-page',
  // '/request-portal',
  // '/manage-employees',
  // '/support-services',
  // '/resume-tips',
  // '/interview-preparation',
  // '/profile',
  // '/upcoming-interviews',
  // '/application-timeline',
  // '/notifications',
  // '/community',
  // '/resources',
  // '/packages',
  // '/about-us',
  // '/companies/hiring',
  // Custom header tabs from client config, e.g. '/p/your-slug'
];

if (typeof window !== 'undefined') {
  window.BLOCK_ACCESS = BLOCK_ACCESS;
}

// Lazy-load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage/HomePage'));
const LoginPage = lazy(() => import('./pages/Login/Login'));
const SignupPage = lazy(() => import('./pages/Signup/Signup'));
const JobSearchPage = lazy(() => import('./pages/JobSearchPage/JobSearchPage'));
const JobsPage = lazy(() => import('./pages/JobsPage/JobsPage'));
const RecommendedJobsPage = lazy(() => import('./pages/RecommendedJobsPage/RecommendedJobsPage'));
const SavedJobsPage = lazy(() => import('./pages/SavedJobsPage/SavedJobsPage'));
const DemoLoginPage = lazy(() => import('./pages/DemoLoginPage/DemoLoginPage'));
const RecruitmentSolutionsPage = lazy(() => import('./pages/RecruitmentSolutionsPage/RecruitmentSolutionsPage'));
const BrandedCompanyPage = lazy(() => import('./pages/BrandedCompanyPage/BrandedCompanyPage'));
const RequestPortalPage = lazy(() => import('./pages/RequestPortalPage/RequestPortalPage'));
const ManageEmployeesPage = lazy(() => import('./pages/ManageEmployeesPage/ManageEmployeesPage'));
const SupportServicesPage = lazy(() => import('./pages/SupportServicesPage/SupportServicesPage'));
const InterviewPreparationPage = lazy(() => import('./pages/InterviewPreparation/InterviewPreparation'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage/NotificationsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));
const UpcomingInterviewsPage = lazy(() => import('./pages/UpcomingInterviewsPage/UpcomingInterviewsPage'));
const ApplicationStatusPage = lazy(() => import('./pages/ApplicationStatusPage/ApplicationStatusPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage/AboutUsPage'));
const JobDescPage = lazy(() => import('./pages/JobDescPage/JobDescPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage/PartnerPage'));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage/CompaniesPage'));
const TestInstructionPage = lazy(() => import('./pages/TestInstructionPage/TestInstructionPage'));
const TestApplication = lazy(() => import('./pages/TestApplication/TestApplication'));

const AllTestsPage = lazy(() => import('./pages/AllTestsPage/AllTestsPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage/CommunityPage'));
const ResumeTipsPage = lazy(() => import('./pages/ResumeTipsPage/ResumeTipsPage'));
const CustomWebpage = lazy(() => import('./pages/CustomPage/CustomWebpage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage/PaymentSuccessPage'));
const PackagesPage = lazy(() => import('./pages/PackagesPage/PackagesPage'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage/ComingSoonPage'));

function BlockAccessGate({ children }) {
  const { pathname } = useLocation();
  if (isPathBlocked(pathname, BLOCK_ACCESS)) {
    return (
      <Suspense fallback={<PageShimmer variant="table" />}>
        <ComingSoonPage />
      </Suspense>
    );
  }
  return children;
}

const wrapWithBlockAccess = (element) => (
  <BlockAccessGate>{element}</BlockAccessGate>
);

// Route configuration array
const routes = [
  { path: '/', element: <HomePageOrPartnerPage HomePageComponent={HomePage} PartnerPageComponent={PartnerPage} /> },
  { path: '/marine-jobs', element: <JobsPage metaTitle="Marine Jobs" metaDescription="Find the best marine jobs and career opportunities with Seahire. We connect certified professionals with top ship owners and maritime companies worldwide." /> },
  { path: '/seafarer-jobs', element: <JobsPage metaTitle="Seafarer Jobs" metaDescription="Seahire is your trusted platform for seafarer jobs. Browse verified openings for captains, engineers, deck officers, and crew members." /> },
  { path: '/crew-hiring', element: <JobsPage metaTitle="Crew Hiring" metaDescription="Efficient crew hiring for ship owners and managers. Seahire streamlines the recruitment process, connecting you with qualified and verified marine crew." /> },
  { path: '/ship-crew-recruitment', element: <JobsPage metaTitle="Ship Crew Recruitment" metaDescription="Seahire offers advanced ship crew recruitment solutions. Connect with skilled seafarers and manage your crewing needs with our technology-driven platform." /> },
  { path: '/about-seahire', element: <AboutUsPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/job-search', element: <JobSearchPage /> },
  { path: '/jobs', element: <JobsPage /> },
  { path: '/recommended-jobs', element: <ProtectedRoute><RecommendedJobsPage /></ProtectedRoute> },
  { path: '/saved-jobs', element: <ProtectedRoute><SavedJobsPage /></ProtectedRoute> },
  { path: '/request-demo', element: <DemoLoginPage /> },
  { path: '/recruitment-solutions', element: <RecruitmentSolutionsPage /> },
  { path: '/branded-company-page', element: <BrandedCompanyPage /> },
  { path: '/request-portal', element: <RequestPortalPage /> },
  { path: '/manage-employees', element: <ProtectedRoute><ManageEmployeesPage /></ProtectedRoute> },
  { path: '/support-services', element: <SupportServicesPage /> },
  { path: '/interview-preparation', element: <InterviewPreparationPage /> },
  { path: '/resume-tips', element: <ResumeTipsPage /> },
  { path: '/notifications', element: <ProtectedRoute><NotificationsPage /></ProtectedRoute> },
  { path: '/profile', element: <ProtectedRoute><ProfilePage /></ProtectedRoute> },
  { path: '/upcoming-interviews', element: <ProtectedRoute><UpcomingInterviewsPage /></ProtectedRoute> },
  { path: '/application-timeline', element: <ProtectedRoute><ApplicationStatusPage /></ProtectedRoute> },
  { path: '/about-us', element: <AboutUsPage /> },
  { path: '/job-desc/:jobId', element: <JobDescPage /> },
  { path: '/partner/:companyId', element: <PartnerPage /> },
  { path: '/companies/hiring', element: <CompaniesPage /> },
  { path: '/test-instruction', element: <ProtectedRoute><TestInstructionPage /></ProtectedRoute> },
  { path: '/test-app', element: <ProtectedRoute><TestApplication /></ProtectedRoute> },
  { path: '/community', element: <ProtectedRoute><CommunityPage /></ProtectedRoute> },
  { path: '/resources', element: <ProtectedRoute><AllTestsPage /></ProtectedRoute> },
  { path: '/packages', element: <ProtectedRoute><PackagesPage /></ProtectedRoute> },
  { path: '/checkout', element: <ProtectedRoute><CheckoutPage /></ProtectedRoute> },
  { path: '/payment/success', element: <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute> },
  // Custom webpages by slug
  { path: '/p/:slug', element: <CustomWebpage /> },
];
// Inner component for routes
function AppRoutes() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isBackNavigationRef = useRef(false);
  const lastPathRef = useRef(location.pathname);
  const isInitialRender = useRef(true);

  // TTL localStorage cleanup: run on load and every 8 hours
  useEffect(() => {
    const clear = startTTLCleanup()
    return clear
  }, [])

  // Detect back navigation synchronously during render
  if (!isInitialRender.current && lastPathRef.current !== location.pathname) {
    isBackNavigationRef.current = navigationType === 'POP';
  }
  lastPathRef.current = location.pathname;

  // Use shimmer fallback during forward navigation, null during back navigation
  const suspenseFallback = isBackNavigationRef.current ? null : <PageShimmer variant="table" />;

  React.useLayoutEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (navigationType === 'POP') {
      setTimeout(() => {
        isBackNavigationRef.current = false;
      }, 500);
    } else {
      isBackNavigationRef.current = false;
    }
  }, [location.pathname, navigationType]);

  // Check if it's a custom published page or custom home
  // If "webpage", show header/footer. If "landing" (default), hide them.
  const client = useClient();
  const { header_tab } = client;
  const isCustomPublishedPage = location.pathname.startsWith('/p/');
  const isHomeRoute = location.pathname === '/';

  const isCheckoutPage = location.pathname === '/checkout';
  const isPaymentSuccessPage = location.pathname === '/payment/success';

  const homeInfo = React.useMemo(() => {
    const raw = client?.home ?? getHomeFromStorage?.() ?? null;
    return raw ? getHomeSlugAndType(raw) : null;
  }, [client?.home]);

  const shouldHideHeaderFooterForHome = isHomeRoute && homeInfo?.slug && homeInfo?.pageType === 'landing';

  const shouldHideHeaderFooter = React.useMemo(() => {
    if (isCheckoutPage || isPaymentSuccessPage) return true;
    if (shouldHideHeaderFooterForHome) return true;
    if (!isCustomPublishedPage) return false;

    try {
      const slug = location.pathname.split('/p/')[1]?.replace(/\/$/, '') || '';
      if (!slug) return true; // Invalid custom path -> hide

      let tabs = [];
      const raw = typeof header_tab === 'string' ? JSON.parse(header_tab) : header_tab;

      if (Array.isArray(raw)) {
        tabs = raw;
      } else if (raw && raw.sub_tabs) {
        tabs = raw.sub_tabs;
      }

      // Find the matching page
      const page = tabs.find(t => {
        // Match either by direct slug property or URL pattern
        if (t.slug === slug) return true;
        if (t.url && t.url.includes(`/p/${slug}`)) return true;
        return false;
      });

      // If page found and type is 'webpage', SHOW header/footer (i.e., DON'T hide)
      if (page && page.type === 'webpage') {
        return false;
      }

      // Default: Hide for landing pages or unknown types
      return true;
    } catch (e) {
      console.error('Error determining page type:', e);
      return true; // Fallback to hidden
    }
  }, [isCheckoutPage, isPaymentSuccessPage, shouldHideHeaderFooterForHome, isCustomPublishedPage, location.pathname, header_tab]);

  return (
    <div className="App">
      {!shouldHideHeaderFooter && <Header />}
      <main className="portal-main" style={{ position: 'relative' }}>
        <Suspense fallback={suspenseFallback}>
          <Routes>
            {routes.map((route, index) => (
              <Route key={index} path={route.path} element={wrapWithBlockAccess(route.element)} />
            ))}
            {/* Catch-all route for 404 - must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!shouldHideHeaderFooter && <Footer />}
    </div>
  );
}
function App() {
  return (
    <ClientProvider>
      <FeaturesProvider>
        <AuthProvider>
          <AlertProvider>
            <GlobalAlert />
            <AppRoutes />
          </AlertProvider>
        </AuthProvider>
      </FeaturesProvider>
    </ClientProvider>
  );
}

export default App;